'use client';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslations } from 'next-intl';
import axios from 'axios';
import { apiPaths } from '@/constants/apiPaths';
import Image from 'next/image';
import { useCaptchaHook } from '@aacn.eu/use-friendly-captcha';
import {
  CaptchaValidationErrorCodes,
  FRIENDLY_CAPTCHA_ENDPOINT,
} from '@/integrations/captcha/constants';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { DataLayerHelper } from '@/utils/data-layer-helper';
import { buildTrackingId } from '@/utils/tracking-helper';
import { GenericContactFormTile } from './types/GenericContactFormTile';
import Container from '../../shared/Container';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import { GenericFormData } from './types/GenericFormData';
import TextInput from '../../shared/inputs/TextInput';
import PrivacyPolicyCheckBox from '../ClubRegistration/components/PrivacyPolicyCheckBox';
import Button from '../../shared/Button';
import GreenCheckmark from '../../../../../public/images/checkmark-green.svg';

export default function GenericContactForm({
  tile,
  trackingPrefix,
}: Readonly<{ tile: GenericContactFormTile; trackingPrefix?: string }>) {
  const t = useTranslations('GenericContactForm');
  const captchaMessages = useTranslations('captchaMessages');
  const { language } = useLocalization();
  const emailTo = tile.email?.text;
  const emailSubject = tile['email-subject']?.text;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const validationSchema = yup.object().shape({
    firstAndLastName: yup.string().required(t('First and last name are required')),
    companyName: yup.string().required(t('Company name is required')),
    email: yup.string().email(t('Invalid email')).required(t('Email is required')),
    message: yup.string().required(t('Message is required')),
    privacyPolicy: yup
      .boolean()
      .oneOf([true], t('You must accept the privacy policy'))
      .required(t('You must accept the privacy policy')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<GenericFormData>({
    resolver: yupResolver(validationSchema),
  });

  const captchaHook = useCaptchaHook({
    siteKey: process.env.NEXT_PUBLIC_FRIENDLY_CAPTCHA_SITE_KEY || '',
    endpoint: FRIENDLY_CAPTCHA_ENDPOINT,
    startMode: 'none',
    language: language as any,
    showAttribution: true,
  });

  // clear error on captcha status change to success
  useEffect(() => {
    if (!captchaHook.captchaStatus.error && captchaHook.captchaStatus.solution) {
      setError('');
    }
  }, [captchaHook.captchaStatus]);

  async function onSubmit(data: GenericFormData) {
    setError('');
    if (captchaHook.captchaStatus.solution === null) {
      setError(captchaMessages('Please complete the CAPTCHA before submitting'));
      return;
    }
    if (emailTo) {
      try {
        setLoading(true);
        const response = await axios.post(
          apiPaths.forms['generic-form'],
          {
            emailTo,
            emailSubject,
            frcCaptchaSolution: captchaHook.captchaStatus.solution,
            ...data,
          },
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          },
        );
        if (response.status === 200) {
          setLoading(false);
          setEmailSent(true);
          DataLayerHelper.pushFormSubmit({ formId: 'generic-contact-form' });
        }
      } catch (requestError: any) {
        setLoading(false);
        console.error('Error sending email:', requestError);

        // Handle CAPTCHA-specific error
        if (requestError.response && requestError.response.status === 400) {
          const serverCaptchaErrors = requestError.response.data.errors;
          if (
            serverCaptchaErrors?.includes(CaptchaValidationErrorCodes.SOLUTION_INVALID) ||
            serverCaptchaErrors?.includes(CaptchaValidationErrorCodes.SOLUTION_TIMEOUT_OR_DUPLICATE)
          ) {
            setError(captchaMessages('Captcha validation failed Please try again'));
            captchaHook.resetWidget();
            return;
          }
        }
        setError(t('Failed to send the email Please try again!'));
      }
    }
  }

  return (
    <Container>
      <div className="mx-auto mb-6 max-w-2xl space-y-3 text-center sm:mb-10 sm:space-y-6">
        <RichTextRenderer blocks={tile.title} className="text-2xl sm:text-3xl" />
        <RichTextRenderer blocks={tile.subtitle} className="sm:text-md" />
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="relative mx-auto max-w-md space-y-3 rounded-lg"
      >
        {emailSent && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 rounded-lg bg-white">
            <h3 className="text-center text-2xl font-bold">{t('Thank you for your inquiry')}</h3>
            <Image
              src={GreenCheckmark}
              sizes="80px"
              alt="Green checkmark"
              className="h-14 w-auto md:h-20"
              width={80}
              height={80}
            />
          </div>
        )}
        <TextInput
          id="firstAndLastName"
          srLabel="First and last name"
          register={register}
          errors={errors.firstAndLastName}
          placeholder={`${t('First And Last Name')}*`}
        />

        <TextInput
          id="companyName"
          srLabel="Company name"
          register={register}
          errors={errors.companyName}
          placeholder={`${t('Company Name')}*`}
        />

        <TextInput
          id="email"
          srLabel="Email"
          register={register}
          errors={errors.email}
          placeholder={`${t('Email')}*`}
        />

        {/* Message textarea */}
        <div>
          <textarea
            id="message"
            {...register('message')}
            className="h- h-50 w-full rounded-xs border border-neutral-50 p-3"
            placeholder={`${t('Message Placeholder')}*`}
          />
          {errors.message && <p className="text-sm text-red">{errors.message.message}</p>}
        </div>

        <PrivacyPolicyCheckBox register={register} errors={errors.privacyPolicy} />

        {captchaHook.CaptchaWidget({
          className: 'relative pb-4',
        })}
        <Button
          disabled={
            captchaHook.captchaStatus.solution === null || captchaHook.captchaStatus.error !== null
          }
          type="submit"
          className="w-full"
          loading={loading}
          data-tile-element-id={buildTrackingId(trackingPrefix, 'submit')}
        >
          {t('Submit')}
        </Button>

        {error && <p className="mt-1 text-center text-sm text-red">{error}</p>}
      </form>
    </Container>
  );
}
